import { Ionicons } from '@expo/vector-icons';
import React, { useMemo } from 'react';
import type { ReactElement } from 'react';
import { FlatList, ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { TeamCrest } from '@/components/matches/TeamCrest';
import { AppText } from '@/components/ui/AppText';
import { Card } from '@/components/ui/Card';
import { useThemePalette } from '@/config/theme';
import { useScores365Standings } from '@/features/competition/hooks';
import type {
  WcStandingDestination,
  WcStandingGroup,
  WcStandingRow,
} from '@/lib/data/provider';
import { formatNumber } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';

// 365 recent-form cues: 1 = win, 2 = loss, 3 = draw (provider doc). Anything
// else (incl. 0/undefined) is ignored rather than rendered as a stray pip.
const FORM_WIN = 1;
const FORM_LOSS = 2;
const FORM_DRAW = 3;

// Fixed numeric-column widths keep the header aligned with every row inside the
// shared horizontal scroller (the full table is wider than a phone, 365-style).
const COL = { pos: 34, team: 150, num: 26, gd: 34, pts: 34, form: 96 } as const;
const NUM_KEYS = ['played', 'won', 'drawn', 'lost', 'gf', 'ga'] as const;
type NumKey = (typeof NUM_KEYS)[number];

const TABLE_WIDTH =
  COL.pos + COL.team + COL.num * NUM_KEYS.length + COL.gd + COL.pts + COL.form;

function numValue(row: WcStandingRow, key: NumKey): number {
  switch (key) {
    case 'won':
      return row.won;
    case 'drawn':
      return row.draw;
    case 'lost':
      return row.lost;
    case 'gf':
      return row.for;
    case 'ga':
      return row.against;
    default:
      return row.played;
  }
}

/** Map a row's destinationNum to its legend entry (color + name), or null. */
function destinationFor(
  row: WcStandingRow,
  destinations: WcStandingDestination[],
): WcStandingDestination | null {
  if (row.destinationNum == null) return null;
  return destinations.find((d) => d.num === row.destinationNum) ?? null;
}

function HeaderRow() {
  const { t } = useTranslation();
  return (
    <View className="flex-row items-center border-b border-line py-2">
      <AppText
        variant="micro"
        weight="medium"
        className="text-center"
        style={{ width: COL.pos }}
      >
        {t('leagues.pos')}
      </AppText>
      <AppText variant="micro" weight="medium" className="ps-2" style={{ width: COL.team }}>
        {t('leagues.team')}
      </AppText>
      {NUM_KEYS.map((key) => (
        <AppText
          key={key}
          variant="micro"
          weight="medium"
          tabular
          className="text-center"
          style={{ width: COL.num }}
        >
          {t(`wcGroups.${key}`)}
        </AppText>
      ))}
      <AppText
        variant="micro"
        weight="medium"
        tabular
        className="text-center"
        style={{ width: COL.gd }}
      >
        {t('leagues.goalDiff')}
      </AppText>
      <AppText
        variant="micro"
        weight="bold"
        tabular
        className="text-center text-ink"
        style={{ width: COL.pts }}
      >
        {t('leagues.points')}
      </AppText>
      <AppText
        variant="micro"
        weight="medium"
        className="ps-1.5"
        style={{ width: COL.form }}
      >
        {t('leagues.form')}
      </AppText>
    </View>
  );
}

/** Recent results as compact colour-coded pips (oldest→newest). */
function FormPips({ form }: { form: number[] }) {
  const cues = form.filter((c) => c === FORM_WIN || c === FORM_LOSS || c === FORM_DRAW).slice(-5);
  if (cues.length === 0) {
    return (
      <View className="flex-row ps-1.5" style={{ width: COL.form }}>
        <AppText variant="micro" className="text-inactive">
          -
        </AppText>
      </View>
    );
  }
  const tone = (cue: number) =>
    cue === FORM_WIN ? 'bg-win' : cue === FORM_LOSS ? 'bg-loss' : 'bg-draw';
  return (
    <View className="flex-row items-center gap-1 ps-1.5" style={{ width: COL.form }}>
      {cues.map((cue, index) => (
        <View key={index} className={`h-2.5 w-2.5 rounded-full ${tone(cue)}`} />
      ))}
    </View>
  );
}

function GroupRow({
  row,
  destination,
  first,
}: {
  row: WcStandingRow;
  destination: WcStandingDestination | null;
  first: boolean;
}) {
  const palette = useThemePalette();
  const { numeralOptions, locale } = useLocale();
  const fmt = (value: number) => formatNumber(value, numeralOptions);
  const teamName = locale === 'ar' ? row.name ?? '' : row.name ?? row.symbolicName ?? '';
  const gd = row.gd > 0 ? `+${fmt(row.gd)}` : fmt(row.gd);

  return (
    <View
      className={`flex-row items-center py-2.5 ${first ? '' : 'border-t border-line/50'}`}
    >
      {/* Thin qualification colour bar (logical LEFT, mirrors under RTL). */}
      <View
        className="absolute bottom-0 start-0 top-0 w-[3px] rounded-full"
        style={{ backgroundColor: destination?.color ?? 'transparent' }}
      />
      <View
        className="flex-row items-center justify-center gap-0.5"
        style={{ width: COL.pos }}
      >
        <AppText variant="micro" weight="bold" tabular className="text-ink">
          {row.position != null ? fmt(row.position) : '-'}
        </AppText>
        {row.trend != null && row.trend > 0 ? (
          <Ionicons name="caret-up" size={9} color={palette.win} />
        ) : row.trend != null && row.trend < 0 ? (
          <Ionicons name="caret-down" size={9} color={palette.loss} />
        ) : null}
      </View>
      <View className="flex-row items-center gap-2 ps-2" style={{ width: COL.team }}>
        <TeamCrest uri={row.crest} name={teamName || '?'} size="sm" />
        <View className="min-w-0 shrink">
          <AppText variant="caption" weight="medium" className="text-ink" numberOfLines={1}>
            {teamName}
          </AppText>
          {row.fifaRank != null ? (
            <View className="mt-0.5 flex-row items-center gap-1">
              <View className="rounded bg-surface-2 px-1 py-px">
                <AppText
                  variant="micro"
                  weight="bold"
                  tabular
                  className="text-muted"
                  style={{ fontSize: 9, lineHeight: 12 }}
                >
                  {fmt(row.fifaRank)}
                </AppText>
              </View>
              <AppText
                variant="micro"
                className="text-inactive"
                style={{ fontSize: 9, lineHeight: 12 }}
              >
                {/* "FIFA" reads identically in both locales — keep it a short cue. */}
                FIFA
              </AppText>
            </View>
          ) : null}
        </View>
      </View>
      {NUM_KEYS.map((key) => (
        <AppText
          key={key}
          variant="micro"
          tabular
          className="text-center text-soft"
          style={{ width: COL.num }}
        >
          {fmt(numValue(row, key))}
        </AppText>
      ))}
      <AppText
        variant="micro"
        weight="medium"
        tabular
        className="text-center text-soft"
        style={{ width: COL.gd }}
      >
        {gd}
      </AppText>
      <AppText
        variant="micro"
        weight="bold"
        tabular
        className="text-center text-ink"
        style={{ width: COL.pts }}
      >
        {fmt(row.points)}
      </AppText>
      <FormPips form={row.recentForm} />
    </View>
  );
}

const GroupCard = React.memo(function GroupCard({
  group,
  destinations,
}: {
  group: WcStandingGroup;
  destinations: WcStandingDestination[];
}) {
  const { t } = useTranslation();
  const groupName =
    group.name ?? (group.num != null ? t('wcGroups.groupNum', { num: group.num }) : '');
  // Only the destinations this group's rows actually map to (avoids printing the
  // whole 32-team legend under a 4-team group).
  const usedDestinations = useMemo(() => {
    const nums = new Set(group.rows.map((r) => r.destinationNum).filter((n) => n != null));
    return destinations.filter((d) => d.num != null && nums.has(d.num));
  }, [group.rows, destinations]);

  return (
    <Card className="overflow-hidden">
      <View className="border-b border-line bg-surface-2/50 px-3 py-2">
        <AppText variant="caption" weight="bold" className="text-volt-bright">
          {groupName}
        </AppText>
      </View>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ paddingHorizontal: 12, minWidth: '100%' }}
      >
        <View style={{ width: TABLE_WIDTH }}>
          <HeaderRow />
          {group.rows.map((row, index) => (
            <GroupRow
              key={row.teamId}
              row={row}
              destination={destinationFor(row, destinations)}
              first={index === 0}
            />
          ))}
        </View>
      </ScrollView>
      {usedDestinations.length > 0 ? (
        <View className="flex-row flex-wrap gap-x-4 gap-y-1.5 border-t border-line/60 px-3 py-2.5">
          {usedDestinations.map((destination) => (
            <View key={destination.num} className="flex-row items-center gap-1.5">
              <View
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: destination.color ?? 'transparent' }}
              />
              <AppText variant="micro" className="text-muted">
                {destination.name}
              </AppText>
            </View>
          ))}
        </View>
      ) : null}
    </Card>
  );
});

/**
 * Rich 365scores WC group standings: the 12 groups (A–L), each a compact glassy
 * table with position + trend, crest (graceful fallback) + name + FIFA-rank
 * chip, the P/W/D/L/GF/GA/GD/Pts numbers, a recent-form pip strip, a thin
 * qualification colour bar on the row's leading edge, and a per-group
 * destination legend. RTL-safe (logical paddings + localized digits) and
 * light/dark via palette tokens. Self-hides until the 365 sync has run.
 */
export function WcGroups365({
  ListHeaderComponent,
  contentContainerStyle,
}: {
  ListHeaderComponent?: ReactElement | null;
  contentContainerStyle?: React.ComponentProps<typeof FlatList>['contentContainerStyle'];
} = {}) {
  const { data } = useScores365Standings();
  const destinations = useMemo(() => data?.destinations ?? [], [data]);
  const validGroups = useMemo(
    () => (data?.groups ?? []).filter((group) => group.rows.length > 0),
    [data],
  );

  // Render every group card lazily through a FlatList — the 12 tables are ~600
  // nodes, so eager mapping into a ScrollView mounted them all on first paint.
  const renderItem = React.useCallback(
    ({ item }: { item: WcStandingGroup }) => (
      <GroupCard group={item} destinations={destinations} />
    ),
    [destinations],
  );

  if (validGroups.length === 0) return ListHeaderComponent ?? null;

  return (
    <FlatList
      data={validGroups}
      keyExtractor={(group, index) => String(group.num ?? group.name ?? index)}
      renderItem={renderItem}
      ListHeaderComponent={ListHeaderComponent}
      ItemSeparatorComponent={GroupSeparator}
      contentContainerStyle={contentContainerStyle}
      className="px-4"
      initialNumToRender={4}
      windowSize={5}
      removeClippedSubviews
      showsVerticalScrollIndicator={false}
    />
  );
}

/** Vertical gap between group cards (replaces the old `gap-4` on the wrapper). */
function GroupSeparator() {
  return <View className="h-4" />;
}
